const BASE_URL = process.env.NX_NEXT_API_CLIENT_BASE_URL ?? `/api`;

export type FetchInput =
  | string[][]
  | Record<string, string | number | null | Record<string, string | number | null>>
  | string
  | URLSearchParams;

type HttpMethod =
  | 'GET'
  | 'HEAD'
  | 'POST'
  | 'PUT'
  | 'DELETE'
  | 'CONNECT'
  | 'OPTIONS'
  | 'TRACE'
  | 'PATCH';

const fetchData = async <T>(
  resourceUrl: string,
  method: HttpMethod,
  init?: RequestInit
): Promise<T> => {
  const response = await fetch(`${BASE_URL}${resourceUrl}`, {
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    method,
    body: init?.body,
    signal: init?.signal,
  });
  if (!response.ok) {
    console.log('NEXT API Error:', response.status, response.statusText);
    throw new Error(response.statusText);
  }
  const data = (await response.json()) as { data: T };
  return data.data;
};

const isEmptyObject = (obj: FetchInput): boolean => {
  return Object.keys(obj).length === 0;
};

const get = <T>(resourceUrl: string, params?: FetchInput, init?: RequestInit): Promise<T> => {
  let url = resourceUrl;

  if (params && !isEmptyObject(params)) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, value === null ? '' : value.toString());
    });
    url += `?${searchParams.toString()}`;
  }

  return fetchData<T>(url, 'GET');
};

const post = <T>(resourceUrl: string, data: FetchInput, init?: RequestInit): Promise<T> => {
  return fetchData<T>(resourceUrl, 'POST', { body: JSON.stringify(data) });
};

const put = <T>(resourceUrl: string, data: FetchInput, init?: RequestInit): Promise<T> => {
  return fetchData<T>(resourceUrl, 'PUT', { body: JSON.stringify(data) });
};

const patch = <T>(resourceUrl: string, data: FetchInput, init?: RequestInit): Promise<T> => {
  return fetchData<T>(resourceUrl, 'PATCH', { body: JSON.stringify(data) });
};

const _delete = <T>(resourceUrl: string, data: FetchInput, init?: RequestInit): Promise<T> => {
  return fetchData<T>(resourceUrl, 'DELETE');
};

export default {
  get,
  post,
  put,
  patch,
  _delete,
};
